import { Toast } from "./Toast";

const CART_COUNT_KEY = "schilliger:cart-count";

/** Base sprite SVG (sans query string) + ancre symbole, ex. `minus`, `plus`. */
function spriteUseHref(symbolId) {
	return `${window.themeVars?.svgUrl?.replace(/\?.*/, "") ?? ""}#${symbolId}`;
}

const Cart = {
	/** @type {HTMLElement|null} */
	drawer: null,
	/** @type {HTMLElement|null} */
	body: null,
	/** @type {HTMLElement|null} */
	footer: null,
	/** @type {HTMLElement|null} */
	totalEl: null,
	/** @type {HTMLElement|null} */
	countBadge: null,
	/** @type {HTMLElement|null} */
	drawerCount: null,

	/** @type {boolean} */
	_isCartPage: false,
	/** @type {boolean} — tiroir déjà peuplé au moins une fois */
	_drawerLoaded: false,

	_nonce: "",
	_ajaxUrl: "",
	_pending: false,

	init() {
		this._nonce = window.themeVars?.cartNonce ?? "";
		this._ajaxUrl = window.themeVars?.ajaxUrl ?? "";

		this._isCartPage = !!document.getElementById("cart-page");

		this.drawer = document.getElementById("cart-drawer");
		this.body = document.getElementById("cart-drawer-body");
		this.footer = document.getElementById("cart-drawer-footer");
		this.totalEl = document.getElementById("cart-drawer-total");
		this.countBadge = document.getElementById("cart-count");
		this.drawerCount = document.getElementById("cart-drawer-count");

		// Compteur panier : le HTML est déjà rendu côté serveur (data-ssr-count) ; sinon repli localStorage
		const ssrRaw = this.countBadge?.dataset?.ssrCount;
		const ssr = ssrRaw !== undefined && ssrRaw !== "" ? parseInt(ssrRaw, 10) : NaN;
		const stored = this._readCount();
		const initial = Number.isFinite(ssr) ? ssr : stored;
		this._updateBadge(initial);
		if (Number.isFinite(ssr)) {
			this._writeCount(ssr);
		}

		this._bindEvents();

		// Page panier : le HTML est déjà rendu côté serveur.
		// On ne refetch pas au chargement (évite un flash si la session AJAX diffère).
		// Les actions qty / remove / coupon mettent à jour via _renderCartPage.
	},

	// ─── localStorage ────────────────────────────────────────────────────────

	_readCount() {
		try {
			const raw = window.localStorage.getItem(CART_COUNT_KEY);
			if (raw === null) return 0;
			const n = parseInt(raw, 10);
			return Number.isFinite(n) && n >= 0 ? n : 0;
		} catch {
			return 0;
		}
	},

	_writeCount(count) {
		try {
			window.localStorage.setItem(CART_COUNT_KEY, String(count));
			// Notifie les autres onglets ouverts
			window.dispatchEvent(new StorageEvent("storage", { key: CART_COUNT_KEY, newValue: String(count) }));
		} catch {
			// localStorage indisponible (navigation privée restrictive), on ignore
		}
	},

	// ─── Visibilité ──────────────────────────────────────────────────────────

	open() {
		if (!this.drawer) return;
		this.drawer.hidden = false;
		document.body.classList.add("cart-is-open");
		this.drawer.focus();

		// Lazy-load : on ne charge le contenu du tiroir que la première ouverture
		if (!this._drawerLoaded) {
			this._showDrawerSkeleton();
			this._fetchAndRender();
		}
	},

	_showDrawerSkeleton() {
		if (!this.body) return;
		this.body.innerHTML = `
			<ul class="cart-skeleton" aria-hidden="true">
				${Array.from(
					{ length: 3 },
					() => `
					<li class="cart-skeleton__item">
						<span class="cart-skeleton__image"></span>
						<span class="cart-skeleton__lines">
							<span class="cart-skeleton__line cart-skeleton__line--name"></span>
							<span class="cart-skeleton__line cart-skeleton__line--price"></span>
							<span class="cart-skeleton__line cart-skeleton__line--qty"></span>
						</span>
					</li>`,
				).join("")}
			</ul>`;
	},

	close() {
		if (!this.drawer) return;
		this.drawer.hidden = true;
		document.body.classList.remove("cart-is-open");
	},

	// ─── Requêtes AJAX ───────────────────────────────────────────────────────

	async _cartRequest(func, data = {}) {
		const params = new URLSearchParams({
			action: "schilliger_cart",
			function: func,
			nonce: this._nonce,
		});
		// Sérialise data en data[key]=value, convention du router PHP
		Object.entries(data).forEach(([k, v]) => params.append(`data[${k}]`, v));

		const res = await fetch(this._ajaxUrl, {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
			body: params,
		});
		return res.json();
	},

	async _fetchAndRender() {
		const res = await this._cartRequest("cart_get");
		if (res.success) this._render(res.data.cart);
	},

	_updateBadge(count) {
		if (this.countBadge) {
			this.countBadge.textContent = count;
			this.countBadge.hidden = count === 0;
		}
	},

	async addToCart(productId, variationId = 0, quantity = 1) {
		if (this._pending) return;
		this._pending = true;

		try {
			const res = await this._cartRequest("cart_add", {
				product_id: productId,
				variation_id: variationId,
				quantity,
			});

			if (res.success) {
				this._render(res.data.cart);
				this.open();
				Toast({ message: res.data.message, state: "success" });
			} else {
				Toast({ message: res.data.message ?? "Erreur inconnue.", state: "danger" });
			}
		} finally {
			this._pending = false;
		}
	},

	async removeItem(cartKey) {
		const res = await this._cartRequest("cart_remove", { cart_key: cartKey });
		if (res.success) {
			this._render(res.data.cart);
			Toast({ message: res.data.message, state: "success" });
		} else {
			Toast({ message: res.data?.message ?? "Erreur.", state: "danger" });
		}
	},

	async updateQty(cartKey, quantity) {
		const res = await this._cartRequest("cart_update", {
			cart_key: cartKey,
			quantity,
		});
		if (res.success) this._render(res.data.cart);
	},

	// ─── Rendu ───────────────────────────────────────────────────────────────

	_render(cart) {
		const count = cart.count ?? 0;

		// Persistance locale + badge header
		this._writeCount(count);
		this._updateBadge(count);

		// Compteur dans le titre du tiroir
		if (this.drawerCount) {
			this.drawerCount.textContent = count > 0 ? `(${count})` : "";
		}

		// Total
		if (this.totalEl) this.totalEl.innerHTML = cart.total ?? "";

		// Footer du tiroir
		if (this.footer) this.footer.hidden = count === 0;

		// Corps du tiroir
		if (this.body) {
			this._drawerLoaded = true;
			if (count === 0) {
				this.body.innerHTML = `<p class="cart-drawer__empty">${window.themeVars?.i18n?.cart_empty ?? "Votre panier est vide."}</p>`;
			} else {
				this.body.innerHTML = `<ul class="cart-drawer__items">${(cart.items ?? []).map((item) => this._itemTpl(item)).join("")}</ul>`;
			}
		}

		// Mise à jour de la page panier (si on y est)
		if (this._isCartPage) {
			this._renderCartPage(cart);
		}
	},

	// ─── Rendu de la page panier ──────────────────────────────────────────────

	_renderCartPage(cart) {
		const count = cart.count ?? 0;

		// Titre / compteur
		const pageCount = document.querySelector(".cart-page__count");
		if (pageCount) {
			pageCount.textContent = count > 0 ? `(${count})` : "";
			pageCount.hidden = count === 0;
		}

		const itemsCol = document.getElementById("cart-page-items");
		const emptyMsg = document.getElementById("cart-page-empty");
		const layout = document.querySelector(".cart-page__layout");

		if (count === 0) {
			if (layout) layout.hidden = true;
			if (emptyMsg) emptyMsg.hidden = false;
			return;
		}

		if (layout) layout.hidden = false;
		if (emptyMsg) emptyMsg.hidden = true;

		if (itemsCol) {
			itemsCol.innerHTML = (cart.items ?? []).map((item) => this._cartPageRowTpl(item)).join("");
		}

		const totalEl = document.getElementById("cart-summary-total");
		if (totalEl && cart.total) totalEl.innerHTML = cart.total;

		this._renderFreeShipping(cart.free_shipping ?? null);
	},

	_renderFreeShipping(fs) {
		const box = document.getElementById("cart-summary-shipping");
		const text = document.getElementById("cart-summary-shipping-text");
		if (!box || !text) return;

		if (!fs?.enabled) {
			box.hidden = true;
			return;
		}

		box.hidden = false;

		if (fs.unlocked) {
			text.textContent = window.themeVars?.i18n?.free_shipping_unlocked ?? "Livraison gratuite offerte !";
			return;
		}

		const remaining = fs.remaining_html ?? "";
		const before = window.themeVars?.i18n?.free_shipping_before ?? "Plus que";
		const after = window.themeVars?.i18n?.free_shipping_after ?? "pour bénéficier de la livraison gratuite";
		text.innerHTML = `${before} <strong>${remaining}</strong> ${after}`;
	},

	_cartPageRowTpl(item) {
		const nameEsc = String(item.name ?? "").replace(/"/g, "&quot;");
		const attrs = (item.attributes ?? []).map((attr) => `<span>${attr}</span>`).join("");
		const attrsHtml = attrs ? `<p class="cart-card__attrs">${attrs}</p>` : "";
		const promoHtml =
			item.on_sale && item.regular_price
				? `<div class="cart-card__promo">
					<span class="cart-card__regular">au lieu de ${item.regular_price}</span>
					<span class="tag tag--secondary">Promo</span>
				</div>`
				: "";
		const image = item.image_large || item.image || "";

		return `
			<li class="cart-card" data-cart-key="${item.key}">
				<a href="${item.permalink}" class="cart-card__image-link" tabindex="-1">
					<img src="${image}" alt="${nameEsc}" class="cart-card__image" width="144" height="144" loading="lazy">
				</a>
				<div class="cart-card__body">
					<div class="cart-card__top">
						<div class="cart-card__info">
							<a href="${item.permalink}" class="cart-card__name">${item.name}</a>
							${attrsHtml}
						</div>
						<div class="cart-card__actions">
							<button
								class="cart-card__wishlist"
								type="button"
								data-product_id="${item.product_id}"
								data-wishlist-product-id="${item.product_id}"
								aria-pressed="false"
								aria-label="Ajouter à la liste de souhaits"
								title="Ajouter à la liste de souhaits"
							>
								<svg class="icon" aria-hidden="true"><use xlink:href="${spriteUseHref("heart")}"></use></svg>
							</button>
							<button
								class="cart-card__remove"
								type="button"
								data-cart-remove
								data-cart-key="${item.key}"
								aria-label="Retirer ${nameEsc}"
							>
								<svg class="icon" aria-hidden="true"><use xlink:href="${spriteUseHref("trash")}"></use></svg>
							</button>
						</div>
					</div>
					<div class="cart-card__bottom">
						<div class="cart-qty">
							<button class="cart-qty__btn" type="button" data-cart-qty-dec data-cart-key="${item.key}" data-qty="${item.quantity}" aria-label="Diminuer">
								<svg class="icon" aria-hidden="true"><use xlink:href="${spriteUseHref("minus")}"></use></svg>
							</button>
							<span class="cart-qty__value">${item.quantity}</span>
							<button class="cart-qty__btn" type="button" data-cart-qty-inc data-cart-key="${item.key}" data-qty="${item.quantity}" aria-label="Augmenter">
								<svg class="icon" aria-hidden="true"><use xlink:href="${spriteUseHref("plus")}"></use></svg>
							</button>
						</div>
						<div class="cart-card__price">
							${promoHtml}
							<span class="cart-card__subtotal">${item.subtotal}</span>
						</div>
					</div>
				</div>
			</li>`;
	},

	_renderCartPageCoupons(coupons) {
		const list = document.getElementById("cart-coupon-list");
		if (!list) return;

		if (coupons.length === 0) {
			list.innerHTML = "";
			return;
		}

		list.innerHTML = coupons
			.map(
				(c) => `
			<li class="cart-coupon__item">
				<span class="cart-coupon__code">${c.code ?? c.raw_code?.toUpperCase()}</span>
				<span class="cart-coupon__discount">−${c.discount}</span>
				<button
					class="cart-coupon__remove btn--icon"
					type="button"
					data-coupon-remove
					data-coupon-code="${c.raw_code}"
					aria-label="Retirer le code ${c.code ?? c.raw_code}"
				>×</button>
			</li>`,
			)
			.join("");
	},

	_itemTpl(item) {
		const imgAlt = item.name.replace(/"/g, "&quot;");
		return `
			<li class="cart-item" data-cart-key="${item.key}">
				<a href="${item.permalink}" class="cart-item__image-link" tabindex="-1">
					<img
						src="${item.image}"
						alt="${imgAlt}"
						class="cart-item__image"
						width="80"
						height="80"
						loading="lazy"
					>
				</a>
				<div class="cart-item__details">
					<a href="${item.permalink}" class="cart-item__name">${item.name}</a>
					<div class="cart-item__subtotal">${item.subtotal}</div>
					<div class="cart-qty">
						<button
							class="cart-qty__btn"
							type="button"
							data-cart-qty-dec
							data-cart-key="${item.key}"
							data-qty="${item.quantity}"
							aria-label="Diminuer la quantité"
						>
							<svg class="icon" aria-hidden="true"><use xlink:href="${spriteUseHref("minus")}"></use></svg>
						</button>
						<span class="cart-qty__value">${item.quantity}</span>
						<button
							class="cart-qty__btn"
							type="button"
							data-cart-qty-inc
							data-cart-key="${item.key}"
							data-qty="${item.quantity}"
							aria-label="Augmenter la quantité"
						>
							<svg class="icon" aria-hidden="true"><use xlink:href="${spriteUseHref("plus")}"></use></svg>
						</button>
					</div>
				</div>
				<button
					class="cart-item__remove btn--icon"
					type="button"
					data-cart-remove
					data-cart-key="${item.key}"
					aria-label="Retirer ${imgAlt} du panier"
				><svg class="icon" aria-hidden="true"><use xlink:href="${spriteUseHref("trash")}"></use></svg></button>
			</li>`;
	},

	// ─── Événements ──────────────────────────────────────────────────────────

	_bindEvents() {
		// Sync cross-onglets : si le panier change dans un autre onglet,
		// on met à jour le badge immédiatement sans AJAX
		window.addEventListener("storage", (e) => {
			if (e.key !== CART_COUNT_KEY) return;
			const count = parseInt(e.newValue ?? "0", 10);
			this._updateBadge(Number.isFinite(count) && count >= 0 ? count : 0);
			// Invalide le cache du tiroir pour forcer un rechargement à la prochaine ouverture
			this._drawerLoaded = false;
		});

		// Ouvrir le tiroir
		document.addEventListener("click", (e) => {
			if (e.target.closest("[data-cart-open]")) this.open();
		});

		// Fermer le tiroir (overlay + bouton fermer)
		document.addEventListener("click", (e) => {
			if (e.target.closest("[data-cart-close]")) this.close();
		});

		// Touche Échap
		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && this.drawer && !this.drawer.hidden) this.close();
		});

		// Boutons "Ajouter au panier" (fiches produit, page produit simple)
		document.addEventListener("click", async (e) => {
			const btn = e.target.closest("[data-cart-add]");
			if (!btn) return;
			e.preventDefault();

			const productId = parseInt(btn.dataset.productId ?? "0", 10);
			const variationId = parseInt(btn.dataset.variationId ?? "0", 10);

			// Sur la page produit, lire la quantité depuis l'input adjacent
			const qtyWrapper = btn.closest(".add-to-cart");
			const qtyInput = qtyWrapper?.querySelector("input[name='quantity']");
			const quantity = parseInt(qtyInput?.value ?? btn.dataset.quantity ?? "1", 10);

			if (!productId) return;

			btn.disabled = true;
			btn.classList.add("is-loading");
			await this.addToCart(productId, variationId, quantity);
			btn.disabled = false;
			btn.classList.remove("is-loading");
		});

		// Formulaire WooCommerce sur la page produit (produit simple + variable)
		// On intercepte le submit natif pour rediriger vers notre AJAX.
		document.addEventListener("submit", async (e) => {
			const form = e.target.closest("form.cart");
			if (!form) return;
			e.preventDefault();

			const productIdInput = form.querySelector("[name='add-to-cart']");
			const variationIdInput = form.querySelector("[name='variation_id']");
			const quantityInput = form.querySelector("[name='quantity']");
			const submitBtn = form.querySelector("[type='submit']");

			const productId = parseInt(productIdInput?.value ?? "0", 10);
			const variationId = parseInt(variationIdInput?.value ?? "0", 10);
			const quantity = parseInt(quantityInput?.value ?? "1", 10);

			if (!productId) return;

			if (submitBtn) {
				submitBtn.disabled = true;
				submitBtn.classList.add("is-loading");
			}

			await this.addToCart(productId, variationId, quantity);

			if (submitBtn) {
				submitBtn.disabled = false;
				submitBtn.classList.remove("is-loading");
			}
		});

		// Retirer un article du panier
		document.addEventListener("click", (e) => {
			const btn = e.target.closest("[data-cart-remove]");
			if (!btn) return;
			this.removeItem(btn.dataset.cartKey);
		});

		// Modifier la quantité (+ / −)
		document.addEventListener("click", async (e) => {
			const dec = e.target.closest("[data-qty-dec], [data-cart-qty-dec]");
			const inc = e.target.closest("[data-qty-inc], [data-cart-qty-inc]");
			const btn = dec ?? inc;
			if (!btn) return;

			const key = btn.dataset.cartKey ?? null;

			// ── Cas page produit : pas de cart-key, on pilote juste l'input ──
			if (!key) {
				const qtyCtrl = btn.closest(".cart-qty, .add-to-cart__qty");
				const input = qtyCtrl?.querySelector("input[name='quantity']");
				if (!input) return;
				const current = parseInt(input.value, 10) || 1;
				const min = parseInt(input.min, 10) || 1;
				input.value = dec ? Math.max(min, current - 1) : current + 1;
				return;
			}

			// ── Cas panier : verrouille le contrôle et envoie l'AJAX ─────────
			const qtyCtrl = btn.closest(".cart-item__qty, .cart-qty");
			if (qtyCtrl) {
				qtyCtrl.classList.add("is-loading");
				qtyCtrl.querySelectorAll("button").forEach((b) => (b.disabled = true));
			}

			const current = parseInt(btn.dataset.qty, 10);
			const next = dec ? Math.max(0, current - 1) : current + 1;

			await this.updateQty(key, next);
			// Le DOM est remplacé par _render(), pas besoin de re-activer manuellement
		});

		// ── Page panier : formulaire coupon ──────────────────────────────────

		document.addEventListener("submit", async (e) => {
			const form = e.target.closest("#cart-coupon-form");
			if (!form) return;
			e.preventDefault();

			const input = form.querySelector("[name='coupon_code']");
			const code = input?.value?.trim() ?? "";
			const btn = form.querySelector("[type='submit']");

			if (!code) return;

			if (btn) {
				btn.disabled = true;
				btn.classList.add("is-loading");
			}

			const res = await this._cartRequest("cart_coupon", {
				coupon_code: code,
				coupon_action: "apply",
			});

			if (btn) {
				btn.disabled = false;
				btn.classList.remove("is-loading");
			}

			if (res.success) {
				if (input) input.value = "";
				this._render(res.data.cart);
				Toast({ message: res.data.message, state: "success" });
			} else {
				Toast({ message: res.data?.message ?? "Code invalide.", state: "danger" });
			}
		});

		// ── Page panier : retirer un coupon ──────────────────────────────────

		document.addEventListener("click", async (e) => {
			const btn = e.target.closest("[data-coupon-remove]");
			if (!btn) return;

			const code = btn.dataset.couponCode ?? "";
			if (!code) return;

			const res = await this._cartRequest("cart_coupon", {
				coupon_code: code,
				coupon_action: "remove",
			});

			if (res.success) {
				this._render(res.data.cart);
				Toast({ message: res.data.message, state: "success" });
			}
		});
	},
};

export { Cart };
